import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShoppingCart } from "lucide-react";
import Button from "../../../components/common/Button";
import Card from "../../../components/common/Card";
import ErrorState from "../../../components/common/ErrorState";
import { CardGridSkeleton } from "../../../components/common/Skeleton";
import { useCart } from "../../../context/CartContext";
import { useToast } from "../../../context/ToastContext";

// 이미지 로드 실패 시 폴백 플레이스홀더
const FALLBACK_IMAGE =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'><rect width='100%' height='100%' fill='#f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#9ca3af' font-size='16' font-family='sans-serif'>이미지 없음</text></svg>`
  );

const PopularWreathsSection = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { showToast } = useToast();

  const [popularWreaths, setPopularWreaths] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | error | success

  const loadWreaths = useCallback(async () => {
    setStatus("loading");
    try {
      const response = await fetch("/mockdata/wreaths.json");
      if (!response.ok) throw new Error("Failed to fetch wreaths");
      const data = await response.json();
      setPopularWreaths(data.slice(0, 4));
      setStatus("success");
    } catch (error) {
      console.error("화환 데이터를 불러오는데 실패했습니다:", error);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    loadWreaths();
  }, [loadWreaths]);

  const handleQuickAdd = (e, wreath) => {
    e.preventDefault();
    e.stopPropagation();
    if (!wreath.inStock) return;
    addToCart(wreath, 1);
    showToast(`'${wreath.name}'을(를) 장바구니에 담았습니다.`, "success");
  };

  return (
    <section className="py-16 bg-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-bold text-charcoal">인기 화환</h2>
          </div>
          <Link to="/wreaths">
            <Button variant="outline" size="sm">
              전체보기 <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>

        {status === "loading" && (
          <CardGridSkeleton count={4} columns={4} withImage={true} />
        )}

        {status === "error" && (
          <ErrorState
            title="인기 화환을 불러오지 못했습니다"
            message="네트워크 상태를 확인한 뒤 다시 시도해주세요."
            onRetry={loadWreaths}
          />
        )}

        {status === "success" && popularWreaths.length === 0 && (
          <Card className="bg-white border border-gray-200">
            <div className="p-8 text-center text-gray-500">
              표시할 화환이 없습니다.
            </div>
          </Card>
        )}

        {status === "success" && popularWreaths.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularWreaths.map((wreath, index) => (
              <Card
                key={wreath.id}
                role="button"
                tabIndex={0}
                aria-label={`${wreath.name} 상세 보기`}
                onClick={() => navigate(`/wreaths/${wreath.id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/wreaths/${wreath.id}`);
                  }
                }}
                padding="none"
                className="h-full hover:shadow-xl transition-all cursor-pointer bg-white border border-gray-200 overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <div className="relative aspect-square overflow-hidden bg-white">
                  {/* 순위 뱃지 */}
                  <span className="absolute left-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-primary-900 text-xs font-bold text-white">
                    {index + 1}
                  </span>
                  {/* 품절 표시 */}
                  {!wreath.inStock && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                      <span className="rounded bg-gray-700 px-3 py-1 text-sm font-semibold text-white">
                        품절
                      </span>
                    </div>
                  )}
                  <img
                    src={wreath.image}
                    alt={wreath.name}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = FALLBACK_IMAGE;
                    }}
                    className={`w-full h-full object-cover ${
                      !wreath.inStock ? "opacity-60" : ""
                    }`}
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-charcoal mb-2 truncate">
                    {wreath.name}
                  </h3>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xl font-bold text-charcoal">
                      {wreath.price.toLocaleString()}원
                    </span>
                  </div>
                  <div className="mb-3 text-sm text-gray-600">
                    <span className="inline-block bg-gray-100 px-2 py-1 rounded">
                      {wreath.category}
                    </span>
                    {wreath.inStock ? (
                      <span className="ml-2 text-green-600">재고 있음</span>
                    ) : (
                      <span className="ml-2 text-gray-400">품절</span>
                    )}
                  </div>
                  {/* 빠른 담기 */}
                  <button
                    type="button"
                    onClick={(e) => handleQuickAdd(e, wreath)}
                    disabled={!wreath.inStock}
                    className="w-full inline-flex items-center justify-center gap-1 rounded-lg bg-primary-900 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:bg-primary-300"
                  >
                    <ShoppingCart size={16} aria-hidden="true" />
                    {wreath.inStock ? "장바구니 담기" : "품절"}
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PopularWreathsSection;
